import { useCallback, useEffect, useMemo, useRef } from "react";
import type { EpochRecord } from "../data/types";
import { stateColor } from "../data/types";
import type { Mode } from "../domain/mode";
import { mjdToYear, yearToMjd } from "../domain/physics";
import type { CanvasPalette } from "../theme/canvasPalette";

interface Props {
  epochs: EpochRecord[];
  cursorMjd: number;
  primaryIndex: number;
  comparisonIndex: number | null;
  mode: Mode;
  onMjdChange: (mjd: number) => void;
  palette: CanvasPalette;
}

const PADDING = { top: 14, right: 24, bottom: 28, left: 24 };

/** Year gridline tick spacing that keeps the axis to roughly 6-9 labels. */
function tickStepYears(spanYears: number): number {
  for (const step of [1, 2, 5, 10, 20, 50]) {
    if (spanYears / step <= 9) return step;
  }
  return 100;
}

/** Build evenly spaced year ticks covering the MJD range. */
export function buildYearTicks(minMjd: number, maxMjd: number): Array<{ mjd: number; label: string }> {
  const startYear = Math.ceil(mjdToYear(minMjd));
  const endYear = Math.floor(mjdToYear(maxMjd));
  const step = tickStepYears(Math.max(1, endYear - startYear));
  const firstTick = Math.ceil(startYear / step) * step;
  const ticks: Array<{ mjd: number; label: string }> = [];
  for (let year = firstTick; year <= endYear; year += step) {
    ticks.push({ mjd: yearToMjd(year), label: String(year) });
  }
  return ticks;
}

export function Timeline(props: Props) {
  const { epochs, cursorMjd, primaryIndex, comparisonIndex, mode, onMjdChange, palette } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const draggingRef = useRef(false);

  const minMjd = Math.min(...epochs.map((e) => e.mjd)) - 200;
  const maxMjd = Math.max(...epochs.map((e) => e.mjd)) + 200;
  const yearTicks = useMemo(() => buildYearTicks(minMjd, maxMjd), [minMjd, maxMjd]);
  const firstYear = Math.round(mjdToYear(minMjd + 200));
  const lastYear = Math.round(mjdToYear(maxMjd - 200));

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    const width = Math.max(1, Math.floor(cssWidth * dpr));
    const height = Math.max(1, Math.floor(cssHeight * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const innerLeft = PADDING.left;
    const innerRight = cssWidth - PADDING.right;
    const innerTop = PADDING.top;
    const innerBottom = cssHeight - PADDING.bottom;
    const innerW = innerRight - innerLeft;

    const mjdToPx = (mjd: number): number =>
      innerLeft + ((mjd - minMjd) / (maxMjd - minMjd)) * innerW;

    ctx.strokeStyle = palette.timelineAxis;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(innerLeft, innerBottom);
    ctx.lineTo(innerRight, innerBottom);
    ctx.stroke();

    ctx.font = "11px Inter, system-ui, sans-serif";
    ctx.fillStyle = palette.axisText;
    for (const tick of yearTicks) {
      if (tick.mjd < minMjd || tick.mjd > maxMjd) continue;
      const px = mjdToPx(tick.mjd);
      ctx.strokeStyle = palette.timelineTick;
      ctx.beginPath();
      ctx.moveTo(px, innerTop);
      ctx.lineTo(px, innerBottom);
      ctx.stroke();
      ctx.fillText(tick.label, px - 12, innerBottom + 14);
    }

    for (const epoch of epochs) {
      const px = mjdToPx(epoch.mjd);
      const color = stateColor(epoch.state);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(px, innerTop);
      ctx.lineTo(px, innerBottom);
      ctx.stroke();

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(px, innerBottom, 4, 0, Math.PI * 2);
      ctx.fill();

      if (epoch.index === primaryIndex) {
        ctx.strokeStyle = palette.timelineSelectionRing;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px, innerBottom, 6, 0, Math.PI * 2);
        ctx.stroke();
      }
      if (epoch.index === comparisonIndex) {
        ctx.strokeStyle = palette.timelineCompareRing;
        ctx.lineWidth = 2;
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.arc(px, innerBottom, 7, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    const cursorPx = mjdToPx(cursorMjd);
    ctx.strokeStyle =
      mode === "interpolated" ? palette.timelineCursorInterp : palette.timelineCursorModern;
    ctx.setLineDash(mode === "interpolated" ? [4, 4] : []);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(cursorPx, innerTop - 4);
    ctx.lineTo(cursorPx, innerBottom + 4);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = palette.timelineTagBg;
    const tagText = `MJD ${cursorMjd.toFixed(0)}`;
    const textWidth = ctx.measureText(tagText).width + 8;
    const tagX = Math.min(cursorPx + 6, innerRight - textWidth);
    ctx.shadowColor = palette.timelineTagShadow;
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;
    ctx.fillRect(tagX, innerTop - 12, textWidth, 16);
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.fillStyle = palette.timelineTagText;
    ctx.fillText(tagText, tagX + 4, innerTop);
  }, [epochs, cursorMjd, primaryIndex, comparisonIndex, minMjd, maxMjd, yearTicks, mode, palette]);

  useEffect(() => {
    draw();
    const observer = new ResizeObserver(() => draw());
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [draw]);

  const handlePointer = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const innerLeft = PADDING.left;
      const innerRight = rect.width - PADDING.right;
      const innerW = Math.max(1, innerRight - innerLeft);
      const t = Math.min(1, Math.max(0, (x - innerLeft) / innerW));
      const mjd = minMjd + t * (maxMjd - minMjd);
      onMjdChange(mjd);
    },
    [minMjd, maxMjd, onMjdChange],
  );

  return (
    <>
      <h2 style={{ margin: "0 0 4px 0", fontSize: 12, color: "var(--text-muted)" }}>
        Timeline (MJD)
      </h2>
      <canvas
        ref={canvasRef}
        className="timeline-canvas"
        onPointerDown={(e) => {
          draggingRef.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          handlePointer(e);
        }}
        onPointerMove={(e) => {
          if (!draggingRef.current) return;
          handlePointer(e);
        }}
        onPointerUp={(e) => {
          draggingRef.current = false;
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        role="slider"
        aria-label="Time scrubber"
        tabIndex={0}
      />
      <div className="timeline-meta">
        <span>{firstYear}</span>
        <span>
          Drag to scrub.
          {mode === "observed"
            ? " Snaps to observed epochs."
            : mode === "interpolated"
              ? " Smooth interpolation between observed epochs."
              : " Compare with reddened bright/intermediate template."}
        </span>
        <span>{lastYear}</span>
      </div>
    </>
  );
}
